import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import type { DbEvent } from "@/types/database";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import Icon from "@/components/feature/Icon";
import SmartImage from "@/components/feature/SmartImage";

export default function EventsSection() {
  const [events, setEvents] = useState<DbEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const { ref: headerRef, style: headerStyle } = useScrollReveal();

  useEffect(() => {
    async function fetchEvents() {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("is_active", true)
        .neq("status", "已結束")
        .order("sort_order", { ascending: true })
        .order("id", { ascending: true })
        .limit(4);
      if (!error && data) {
        setEvents(data as DbEvent[]);
      }
      setLoading(false);
    }
    fetchEvents();
  }, []);

  return (
    <section className="py-24 md:py-32 bg-silver-50">
      <div className="px-6 md:px-10 lg:px-16">
        {/* Section header */}
        <div ref={headerRef} style={headerStyle} className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12 md:mb-16">
          <div>
            <p className="text-sm font-medium tracking-[0.2em] text-navy-700 uppercase mb-3">
              Upcoming Events
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-950">
              近期活動
            </h2>
          </div>
          <Link
            to="/events"
            className="text-sm font-medium text-navy-950 hover:text-navy-700 transition-colors inline-flex items-center gap-1 whitespace-nowrap group"
          >
            查看全部
            <Icon className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <Icon className="ri-loader-4-line animate-spin text-2xl text-silver-400 inline-block mb-2" />
            <p className="text-sm text-silver-400">載入中...</p>
          </div>
        ) : events.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Featured event - spans 2 cols on lg */}
            <article className="group md:col-span-2 lg:col-span-2 relative rounded-3xl overflow-hidden bg-navy-950 min-h-[320px] md:min-h-[400px] flex flex-col justify-end hover:shadow-2xl transition-shadow duration-500">
              <SmartImage
                src={events[0].image || ""}
                alt={events[0].title}
                className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
                fallbackText={events[0].category}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/50 to-transparent" />

              <div className="relative z-10 p-6 md:p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full">
                    {events[0].category}
                  </span>
                  <span
                    className={`text-xs font-medium px-3 py-1.5 rounded-full ${
                      events[0].status === "報名中"
                        ? "bg-white text-navy-950"
                        : "bg-white/20 text-white"
                    }`}
                  >
                    {events[0].status}
                  </span>
                </div>
                <p className="text-silver-300 text-sm mb-2">
                  {events[0].date} · {events[0].location}
                </p>
                <h3 className="text-white font-semibold text-xl md:text-2xl lg:text-3xl mb-3 font-serif">
                  {events[0].title}
                </h3>
                <p className="text-silver-300 text-sm md:text-base max-w-xl leading-relaxed">
                  {events[0].description || ""}
                </p>
              </div>
            </article>

            {/* Smaller events */}
            {events.slice(1).map((event) => (
              <article
                key={event.id}
                className="group bg-white rounded-3xl overflow-hidden border border-silver-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <SmartImage
                    src={event.image || ""}
                    alt={event.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                    fallbackText={event.category}
                  />
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="inline-block bg-navy-950/80 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full">
                      {event.category}
                    </span>
                    <span
                      className={`text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-sm ${
                        event.status === "報名中"
                          ? "bg-white text-navy-950"
                          : "bg-white/80 text-navy-800"
                      }`}
                    >
                      {event.status}
                    </span>
                  </div>
                </div>

                <div className="p-5 md:p-6">
                  <p className="text-navy-700 font-semibold text-sm mb-2">
                    {event.date}
                  </p>
                  <h3 className="text-navy-950 font-semibold text-base md:text-lg mb-2 line-clamp-2 group-hover:text-navy-700 transition-colors">
                    {event.title}
                  </h3>
                  <p className="text-silver-500 text-sm line-clamp-2 leading-relaxed mb-4">
                    {event.description || ""}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-silver-400 flex items-center gap-1">
                      <Icon className="ri-map-pin-line" />
                      {event.location}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-navy-950 group-hover:text-navy-700 transition-colors">
                      瞭解更多
                      <Icon className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Icon className="ri-calendar-event-line text-5xl text-silver-300 mb-4" />
            <p className="text-silver-500 text-lg">暫無近期活動</p>
          </div>
        )}
      </div>
    </section>
  );
}